import Seo from "../components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const SECTIONS = [
  {
    title: "General",
    qs: [
      { q: "What is DealVest?", a: "DealVest is a marketplace and AI platform that connects business owners, investors, buyers, and strategic partners across Africa and the Middle East. We help you buy a business, sell yours, raise capital, value an idea, or find investors — all from one place." },
      { q: "How does DealVest work?", a: "Sign up free. Tell us what you want to do — buy, sell, raise, or evaluate. Our AI matches you with the most relevant opportunities or counter-parties, and you can move from idea to first meeting in under 24 hours." },
      { q: "Is DealVest free?", a: "Yes — registration, browsing, AI valuations, idea evaluations and the AI advisor are free. Premium services like featured listings, priority verification, and dedicated transaction support are available on a fee or success-fee basis." },
    ],
  },
  {
    title: "Valuation",
    qs: [
      { q: "How is valuation calculated?", a: "We blend three industry-standard methods: revenue multiples, profit (EBITDA-style) multiples, and a simplified DCF — adjusted for regional comparables in Africa and the Middle East. Our AI synthesizes all three in seconds." },
      { q: "How accurate are valuations?", a: "Our valuations give you a credible range (low / mid / high) suitable for early-stage planning, investor conversations, and negotiation prep. For a binding transaction we still recommend an independent valuation from a licensed advisor." },
      { q: "Can I value an idea?", a: "Yes. The Idea Evaluator analyzes a verbal or written idea — including global comparables, market opportunity, revenue models, business plan, and a pre-revenue valuation range." },
    ],
  },
  {
    title: "Buying & Selling",
    qs: [
      { q: "How do I list a business?", a: "Sign up, open your Dashboard, and click 'New Listing' under My Listings. You can list in under 5 minutes. Verification typically takes 48-72 hours." },
      { q: "How do I contact buyers?", a: "When buyers express interest, you'll receive a notification in your Messages tab. You can review their profile and choose to share NDA-gated information." },
      { q: "How do I contact sellers?", a: "From any listing page, click 'Request Information'. The seller will receive your request and reply via DealVest." },
    ],
  },
  {
    title: "Capital Raising",
    qs: [
      { q: "How do I find investors?", a: "Sign up as a founder, complete your profile (industry, country, stage, ask), and visit /matches. Our AI matches you to active investors based on industry, geography, and ticket size." },
      { q: "How long does fundraising take?", a: "From a quality DealVest profile, investor first-meetings typically happen within 7-14 days. Term sheets generally land within 6-10 weeks for ready founders." },
    ],
  },
  {
    title: "Security",
    qs: [
      { q: "Is my information private?", a: "Yes. Sensitive financials and identity details are gated behind NDAs and stored in bank-grade encrypted infrastructure. You control who sees what." },
      { q: "Are listings verified?", a: "Yes. Every listing undergoes financial sanity-check, legal entity verification, and ownership confirmation before approval." },
    ],
  },
  {
    title: "AI Features",
    qs: [
      { q: "How does the AI valuation work?", a: "Our AI uses Claude Sonnet 4.5 trained on regional dealmaking patterns. It blends revenue multiples, profit multiples, and a DCF estimate — then explains its reasoning." },
      { q: "Can I submit information by voice note?", a: "Yes. On the Valuation and Idea Evaluator pages, you can record a voice note instead of typing. We transcribe it via OpenAI Whisper and feed it into the AI analysis." },
      { q: "Can I use WhatsApp?", a: "A WhatsApp experience is coming soon. You'll be able to register, list, value, raise, and chat with our AI advisor directly via WhatsApp — designed for emerging markets." },
    ],
  },
];

export default function FAQPage() {
  return (
    <div data-testid="faq-page" className="max-w-[820px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <Seo
        title="FAQ — DealVest Frequently Asked Questions"
        description="Answers about buying, selling, raising capital, AI valuation, security, and DealVest's WhatsApp + voice features."
        path="/faq"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": SECTIONS.flatMap(s => s.qs).map(({ q, a }) => ({
            "@type": "Question", "name": q, "acceptedAnswer": { "@type": "Answer", "text": a }
          }))
        }}
      />
      <span className="dv-eyebrow">FAQ</span>
      <h1 className="dv-display text-4xl md:text-6xl my-4">Common questions.</h1>
      <p className="text-lg text-slate-600 mb-12">Quick answers about how DealVest works.</p>

      {SECTIONS.map((sec, si) => (
        <div key={si} className="mb-10">
          <h2 className="font-bold text-xl text-blue-600 mb-3">{sec.title}</h2>
          <Accordion type="single" collapsible className="w-full">
            {sec.qs.map((f, i) => (
              <AccordionItem key={i} value={`${si}-${i}`} className="border-slate-200" data-testid={`faq-${si}-${i}`}>
                <AccordionTrigger className="text-lg font-semibold text-left text-slate-900 hover:text-blue-600 hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed text-[15px] pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      ))}
    </div>
  );
}
